/**
 * Deployment mode — B2B / B2C surface routing (P-B2B-1; canon Doc 25, Doc 31 §6).
 *
 * ## The distinction this module enforces
 *
 * **Deployment mode (this file) is NOT UI mode.** Deployment mode says *which
 * surface families exist* for a workspace. UI mode (Doc 26, `uiMode.ts`) says
 * *how complex one surface is* — B Mode vs C Mode. A B2B workspace can render
 * both B and C Mode; B2C is not "always C Mode". Never conflate them.
 *
 * ## Why an allow-list, not a deny-list
 *
 * Doc 25 §2 enumerates B2B-only screen groups, but Doc 31 §6 states the rule
 * positively: a B2C workspace exposes its installed App route families,
 * participant surfaces, notifications, and personal settings — *nothing else*.
 * We implement the positive form because it **fails closed**: a B2B route added
 * next month is blocked from B2C by default. A deny-list fails open — every new
 * organizational surface would leak into B2C until someone remembered to list
 * it, which is precisely the kind of silent regression a deployment boundary
 * must not have.
 *
 * The App route families come from `APP_REGISTRY`, so the allow-list extends
 * itself whenever an App is registered — declarative, per Doc 25 §6.
 *
 * Everything here is pure and unit-tested; the React binding lives in
 * `useDeploymentMode` / `RequireDeploymentSurface`.
 */

import { APP_REGISTRY } from '@/apps/registry';

export type DeploymentMode = 'b2b' | 'b2c';

/** Default for every existing workspace: today's full organizational product. */
export const DEFAULT_DEPLOYMENT_MODE: DeploymentMode = 'b2b';

/**
 * Non-App surfaces a B2C workspace may reach, beyond the App route families.
 *
 * - participant surfaces: the standalone participant dashboard/room and the
 *   B2C-safe singular session room (`/prioritization/session/:id`), plus the
 *   tokened group-join flow;
 * - public / auth / token-gated surfaces, which are not workspace-scoped at all
 *   and must never be stripped (blocking them would lock users out of login);
 * - personal settings and notifications.
 */
export const B2C_ALLOWED_PREFIXES: readonly string[] = [
  // Participant surfaces
  '/sessions',
  '/prioritization/session', // singular — the participant room, not /sessions/* facilitation
  '/groups',
  // Public / auth / token-gated (never workspace-stripped)
  '/auth',
  '/invite',
  '/trust',
  '/brief/submit',
  '/brief/view',
  // Personal surfaces
  '/notifications',
  '/profile',
  '/settings/profile',
  '/settings/notifications',
  '/my-notes',
];

/** Where a B2C user lands when they hit a B2B-only surface. */
export const B2C_HOME = '/apps';

/**
 * Split a path into segments, ignoring query/hash and trailing slashes.
 * Segment-aware matching matters: `/prioritization/session/abc` (participant
 * room, B2C-safe) must not be captured by a `/prioritization/sessions` rule.
 */
function segments(path: string): string[] {
  const clean = path.split(/[?#]/)[0];
  return clean.split('/').filter(Boolean);
}

/** True when `path` is exactly `prefix` or nested beneath it, segment-wise. */
export function pathMatchesPrefix(path: string, prefix: string): boolean {
  const p = segments(path);
  const q = segments(prefix);
  if (q.length === 0) return false;
  if (p.length < q.length) return false;
  return q.every((seg, i) => p[i] === seg);
}

/** Every route family a B2C workspace may reach: registered Apps + the fixed list. */
export function b2cAllowedPrefixes(): string[] {
  return [
    B2C_HOME,
    ...APP_REGISTRY.map((app) => app.routeFamily),
    ...B2C_ALLOWED_PREFIXES,
  ];
}

/**
 * Whether a B2C workspace may render this path. Fail-closed: anything not
 * explicitly allowed is a B2B-only surface and is blocked at the router.
 */
export function isB2cAllowedPath(path: string): boolean {
  // The root path resolves elsewhere (it redirects); never block it here or the
  // redirect can never run.
  if (segments(path).length === 0) return true;
  return b2cAllowedPrefixes().some((prefix) => pathMatchesPrefix(path, prefix));
}

/** Normalize whatever the workspace row carries into a known mode. */
export function resolveDeploymentMode(value: unknown): DeploymentMode {
  return value === 'b2c' ? 'b2c' : DEFAULT_DEPLOYMENT_MODE;
}

/**
 * The routing decision for one (mode, path) pair.
 * `null` means "render normally".
 */
export function deploymentRedirectFor(
  mode: DeploymentMode,
  path: string,
): string | null {
  if (mode !== 'b2c') return null;
  if (isB2cAllowedPath(path)) return null;
  return B2C_HOME;
}
